import {
  IAiStreamerGameCreatePayload,
  IAiStreamerGameUpdatePayload,
  TAiStreamerGameData,
  TAiStreamerTemplate,
  IAiStreamerJobDetailsRecord,
  IAiStreamerJobDetailsResponse,
  IAiStreamerJobsPayload,
  IAiStreamerJobsStatsData,
  TAiStreamerAvatarRecord,
  TAiStreamerScriptRecord,
  TAiStreamerScriptPayload,
  TCopyStakeGlobalAiStreamerConfigurationResponse,
  TCopyStakeGlobalAiStreamerConfigurationPayload,
  TCopyStakeOperatorAiStreamerConfigurationPayload,
  TCopyStakeOperatorAiStreamerConfigurationResponse,
  EAiStreamerAvatarService,
  TAiStreamerVoiceRecord
} from '@/api/schema';
import { http } from '@/api/Connect';
import { API_VERSION, BASE, BASE_OPERATOR } from '@/api/CopyStake/BaseUrls';
import { AxiosResponse } from 'axios';
import { buildUrl } from '@/helpers/buildUrl';
import {
  EAiStreamerActiveStatus,
  TAiStreamer,
  TAiStreamerFilter,
  TAiStreamerListItem,
  TAiStreamerPayload
} from '@/views/CopyStake/CopyStakeAiStreamer/types';

const buildAutoclickerUrl = (
  type: string,
  version: API_VERSION = API_VERSION.V1
): string => {
  return buildUrl(`${BASE(version)}/{operatorId}/autoclicker/${type}`);
};
const buildAiStreamerUrl = (
  type: string,
  version: API_VERSION = API_VERSION.V2
): string => {
  return buildUrl(`${BASE(version)}/{operatorId}/ai-streamer/${type}`);
};

const API_TEMPLATES_LINK = (): string => buildAutoclickerUrl('templates');
const API_GAMES_LINK = (): string => buildAutoclickerUrl('games');
const API_JOBS_LINK = (): string => buildAutoclickerUrl('jobs');
const API_SCRIPT_LINK = (): string => buildAutoclickerUrl('script');

const API_LIST_V2_LINK = (): string =>
  buildAutoclickerUrl('config/list', API_VERSION.V2);
const API_CONFIG_V2_LINK = (): string =>
  buildAutoclickerUrl('config', API_VERSION.V2);

const API_AI_STREAMER_VOICES = (): string => buildAiStreamerUrl('voices');
const API_AI_STREAMER_AVATARS = (): string =>
  `${BASE(API_VERSION.V2)}/ai-streamer/avatars`;

export const getAiStreamerTemplates = (): Promise<TAiStreamerTemplate[]> => {
  return http
    .get(API_TEMPLATES_LINK(), {
      cacheTime: 5000
    })
    .then(({ data }: AxiosResponse) => data);
};

export const getAiStreamerGames = (): Promise<TAiStreamerGameData[]> => {
  return http
    .get(API_GAMES_LINK(), {
      cacheTime: 2000
    })
    .then(({ data }: AxiosResponse): TAiStreamerGameData[] => data);
};

export const createAiStreamerGame = (
  payload: IAiStreamerGameCreatePayload
): Promise<TAiStreamerGameData> => {
  return http
    .post(API_GAMES_LINK(), payload)
    .then(({ data }: AxiosResponse): TAiStreamerGameData => data);
};

export const updateAiStreamerGame = (
  id: number | string,
  payload: IAiStreamerGameUpdatePayload
): Promise<TAiStreamerGameData> => {
  return http
    .put(`${API_GAMES_LINK()}/${id}`, payload)
    .then(({ data }: AxiosResponse): TAiStreamerGameData => data);
};

export const deleteAiStreamerGame = (id: number | string): Promise<unknown> => {
  return http.delete(`${API_GAMES_LINK()}/${id}`);
};

export const getAiStreamerJobs = (
  params: IAiStreamerJobsPayload
): Promise<IAiStreamerJobDetailsResponse> => {
  return http
    .get(API_JOBS_LINK(), {
      params,
      cacheTime: 0
    })
    .then(({ data }: AxiosResponse): IAiStreamerJobDetailsResponse => data);
};

export const getAiStreamerJobDetails = (
  jobId: number | string
): Promise<IAiStreamerJobDetailsRecord> => {
  return http
    .get(`${API_JOBS_LINK()}/${jobId}`, {
      cacheTime: 0
    })
    .then(({ data }: AxiosResponse): IAiStreamerJobDetailsRecord => data);
};

export const getAiStreamerJobsStats = (): Promise<IAiStreamerJobsStatsData> => {
  return http
    .get(`${API_JOBS_LINK()}/statistics`, {
      cacheTime: 0
    })
    .then(({ data }: AxiosResponse): IAiStreamerJobsStatsData => data);
};

export const stopAiStreamerJob = (
  jobId: number | string,
  force: boolean
): Promise<unknown> => {
  return http
    .post(`${API_JOBS_LINK()}/${jobId}/stop`, null, { params: { force } })
    .then(({ data }: AxiosResponse): unknown => data);
};

export const getAiStreamerAvatarsList = (): Promise<
  TAiStreamerAvatarRecord[]
> => {
  return http
    .get(API_AI_STREAMER_AVATARS(), {
      cacheTime: 5000
    })
    .then(({ data }: AxiosResponse): TAiStreamerAvatarRecord[] => data);
};

export const getAiStreamerScriptList = (
  language: string
): Promise<TAiStreamerScriptRecord[]> => {
  return http
    .get(`${API_SCRIPT_LINK()}/language/${language}`, {
      cacheTime: 0
    })
    .then(({ data }: AxiosResponse): TAiStreamerScriptRecord[] => data);
};

export const getAiStreamerScript = (
  id: number | string
): Promise<TAiStreamerScriptRecord> => {
  return http
    .get(`${API_SCRIPT_LINK()}/${id}`, {
      cacheTime: 0
    })
    .then(({ data }: AxiosResponse): TAiStreamerScriptRecord => data);
};

export const createAiStreamerScript = (
  payload: TAiStreamerScriptPayload
): Promise<TAiStreamerScriptRecord> => {
  return http
    .post(API_SCRIPT_LINK(), payload)
    .then(({ data }: AxiosResponse): TAiStreamerScriptRecord => data);
};

export const updateAiStreamerScript = (
  id: number | string,
  payload: TAiStreamerScriptPayload
): Promise<TAiStreamerScriptRecord> => {
  return http
    .put(`${API_SCRIPT_LINK()}/${id}`, payload)
    .then(({ data }: AxiosResponse): TAiStreamerScriptRecord => data);
};

export const deleteAiStreamerScript = (
  id: number | string
): Promise<unknown> => {
  return http.delete(`${API_SCRIPT_LINK()}/${id}`);
};

export const createAiStreamerConfig = (
  payload: TAiStreamerPayload
): Promise<TAiStreamer> => {
  return http
    .post(API_CONFIG_V2_LINK(), payload)
    .then(({ data }: AxiosResponse<TAiStreamer>) => data);
};

export const updateAiStreamerConfig = (
  payload: TAiStreamer
): Promise<TAiStreamer> => {
  return http
    .put(`${API_CONFIG_V2_LINK()}/${payload.id}`, payload)
    .then(({ data }: AxiosResponse<TAiStreamer>) => data);
};

export const getAiStreamerConfigsList = (
  params: TAiStreamerFilter
): Promise<TAiStreamerListItem[]> => {
  return http
    .get(API_LIST_V2_LINK(), {
      params,
      cacheTime: 0
    })
    .then(({ data }: AxiosResponse): TAiStreamerListItem[] => data);
};

export const cancelStreamAiStreamerConfig = (
  payload: TAiStreamerListItem
): Promise<void> => {
  return http.patch(`${API_CONFIG_V2_LINK()}/${payload.id}/status`, null, {
    params: {
      status: EAiStreamerActiveStatus.INACTIVE
    }
  });
};

export const deleteAiStreamerConfig = (
  id: number | string
): Promise<unknown> => {
  return http.delete(`${API_CONFIG_V2_LINK()}/${id}`);
};

export const getGlobalAiStreamerConfig =
  (): Promise<TCopyStakeGlobalAiStreamerConfigurationResponse> => {
    return http
      .get(`${BASE()}/ai-streamer-config/global`)
      .then(
        ({
          data
        }: AxiosResponse): TCopyStakeGlobalAiStreamerConfigurationResponse =>
          data
      );
  };

export const updateGlobalAiStreamerConfig = (
  payload: TCopyStakeGlobalAiStreamerConfigurationPayload
): Promise<TCopyStakeGlobalAiStreamerConfigurationResponse> => {
  return http
    .put(`${BASE()}/ai-streamer-config/global`, payload)
    .then(
      ({
        data
      }: AxiosResponse): TCopyStakeGlobalAiStreamerConfigurationResponse => data
    );
};

export const getOperatorAiStreamerConfig =
  (): Promise<TCopyStakeOperatorAiStreamerConfigurationResponse> => {
    return http
      .get(`${BASE_OPERATOR()}/ai-streamer-config`)
      .then(
        ({
          data
        }: AxiosResponse): TCopyStakeOperatorAiStreamerConfigurationResponse =>
          data
      );
  };

export const updateOperatorAiStreamerConfig = (
  payload: TCopyStakeOperatorAiStreamerConfigurationPayload
): Promise<TCopyStakeOperatorAiStreamerConfigurationResponse> => {
  return http
    .put(`${BASE_OPERATOR()}/ai-streamer-config`, payload)
    .then(
      ({
        data
      }: AxiosResponse): TCopyStakeOperatorAiStreamerConfigurationResponse =>
        data
    );
};

export const getAiStreamerVoices = (
  avatarService: EAiStreamerAvatarService,
  language: string
): Promise<TAiStreamerVoiceRecord[]> => {
  return http
    .get(API_AI_STREAMER_VOICES(), {
      params: { avatarService, language }
    })
    .then(({ data }: AxiosResponse): TAiStreamerVoiceRecord[] => data);
};

export const getAiStreamerVoiceLanguages = (
  avatarService: EAiStreamerAvatarService
): Promise<string[]> => {
  return http
    .get(`${API_AI_STREAMER_VOICES()}/languages`, {
      params: { avatarService }
    })
    .then(({ data }: AxiosResponse): string[] => data);
};

export const getAiStreamerVoicesAvatarServices = (): Promise<
  EAiStreamerAvatarService[]
> => {
  return http
    .get(`${API_AI_STREAMER_VOICES()}/avatar-services`)
    .then(({ data }: AxiosResponse): EAiStreamerAvatarService[] => data);
};
